import { mockFlights } from '../data/mockFlights';
import { METRO_AREAS_BY_CODE } from '../data/metroAreas';

const CABIN_MULTIPLIERS = {
  Economy: 1,
  'Premium Economy': 1.8,
  Business: 3.2,
  First: 5.5,
};

/**
 * Simulates a flight search API call.
 * Supports one-way trips, cabin multipliers, and multi-airport metro areas.
 *
 * @param {Object} params - Search parameters
 * @param {string} params.origin - Origin airport code or metro code
 * @param {string} params.destination - Destination airport code or metro code
 * @param {string} params.departureDate - Departure date
 * @param {string} [params.returnDate] - Return date
 * @param {string} [params.cabin] - Cabin class
 * @param {string} [params.tripType] - 'round-trip' or 'one-way'
 * @param {number} [params.adults] - Number of adult passengers
 * @param {number} [params.children] - Number of child passengers
 * @param {Object} [params.originData] - Full origin object (may include metro info)
 * @param {Object} [params.destinationData] - Full destination object (may include metro info)
 * @returns {Promise<Array>} Array of flight objects
 */
export function searchFlights(params) {
  return new Promise((resolve) => {
    setTimeout(() => {
      const cabinMult = CABIN_MULTIPLIERS[params.cabin] || 1;
      const isOneWay = params.tripType === 'one-way';

      // Resolve origins: metro area → multiple airports, else single
      const origins = resolveAirports(params.origin, params.originData);
      const destinations = resolveAirports(params.destination, params.destinationData);

      const allResults = [];
      const seen = new Set();

      for (const orig of origins) {
        for (const dest of destinations) {
          const flights = mockFlights.map((flight) => {
            const basePrice = flight.price * cabinMult * (isOneWay ? 0.5 : 1);
            // Add slight price variation for different airport pairs
            const variation = (orig.charCodeAt(0) + dest.charCodeAt(0)) % 50 - 25;
            return {
              ...flight,
              departure: {
                ...flight.departure,
                airport: orig,
                date: params.departureDate || flight.departure.date,
              },
              arrival: {
                ...flight.arrival,
                airport: dest,
              },
              cabin: params.cabin || flight.cabin,
              price: Math.round(basePrice + variation),
            };
          });

          for (const f of flights) {
            // Deduplicate by flight number, keep cheapest
            if (!seen.has(f.flightNumber) || f.price < allResults.find(r => r.flightNumber === f.flightNumber)?.price) {
              if (seen.has(f.flightNumber)) {
                const idx = allResults.findIndex(r => r.flightNumber === f.flightNumber);
                if (idx !== -1) allResults[idx] = f;
              } else {
                seen.add(f.flightNumber);
                allResults.push(f);
              }
            }
          }
        }
      }

      resolve(allResults);
    }, 1500);
  });
}

function resolveAirports(code, data) {
  // If data has metro type, use its airports array
  if (data?.type === 'metro' && data.airports?.length) {
    return data.airports;
  }
  // Check if the code is a known metro area
  const metro = METRO_AREAS_BY_CODE.get(code);
  if (metro) return metro.airports;
  // Single airport
  return [code];
}

/**
 * Returns the optimized payment strategy for a given flight.
 *
 * @param {Object} flight - A flight object
 * @returns {Promise<Object>} Strategy recommendation
 */
export function getPaymentStrategy(flight) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        cardName: 'Chase Sapphire Reserve',
        cardNetwork: 'Visa',
        pointsMultiplier: '3x',
        estimatedSavings: Math.round(flight.price * 0.05),
        explanation: `Earn 3x points on travel purchases with Chase Sapphire Reserve. Combined with the annual travel credit, you save an estimated $${Math.round(flight.price * 0.05)} on this booking.`,
      });
    }, 500);
  });
}
